import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import MappingList from '@components/mappings/MappingList';
import { mappingApi } from '@services/api';
import type { MappingConfig, SourceSystem } from '@types';

export default function Mappings() {
  const navigate = useNavigate();
  const [mappings, setMappings] = useState<MappingConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadMappings();
  }, []);

  const loadMappings = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await mappingApi.getAllMappings();
      setMappings(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load mappings';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (id: string) => {
    navigate(`/mappings/${id}`);
  };

  const handleDelete = async (id: string) => {
    try {
      await mappingApi.deleteMapping(id);
      toast.success('Mapping deleted successfully');
      // Remove from local state
      setMappings(prev => prev.filter(m => m.id !== id));
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to delete mapping';
      toast.error(errorMessage);
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      const original = await mappingApi.getMappingById(id);
      const duplicateData = {
        name: `${original.name} (Copy)`,
        source_system: original.source_system,
        field_mappings: original.field_mappings,
        is_default: false,
      };
      const newMapping = await mappingApi.createMapping(duplicateData);
      toast.success('Mapping duplicated successfully');
      setMappings(prev => [...prev, newMapping]);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to duplicate mapping';
      toast.error(errorMessage);
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      const updatedMapping = await mappingApi.setDefaultMapping(id);
      toast.success('Default mapping updated');
      // Update local state - set this one as default and unset others for same system
      setMappings(prev =>
        prev.map(m => {
          if (m.id === id) {
            return { ...m, is_default: true };
          } else if (m.source_system === updatedMapping.source_system) {
            return { ...m, is_default: false };
          }
          return m;
        })
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to set default mapping';
      toast.error(errorMessage);
    }
  };

  const handleCreate = () => {
    navigate('/mappings/new');
  };

  // Transform data for the list component
  const mappingListData = mappings.map(mapping => ({
    id: mapping.id,
    name: mapping.name,
    source_system: mapping.source_system as SourceSystem,
    is_default: mapping.is_default,
    field_count: mapping.field_mappings
      ? Object.keys(mapping.field_mappings).length
      : 0,
    created_at: mapping.inserted_date,
    updated_at: mapping.updated_date,
  }));

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading mappings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="rounded-full bg-red-100 p-3 inline-block">
            <svg
              className="h-12 w-12 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            Error loading mappings
          </h3>
          <p className="mt-2 text-gray-600">{error}</p>
          <button
            onClick={loadMappings}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <MappingList
          mappings={mappingListData}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
          onSetDefault={handleSetDefault}
          onCreate={handleCreate}
        />
      </div>
    </div>
  );
}
